import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPanelTitle,
  AdminTabButton,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminStatusBadge,
  AdminFlagStatusBadge,
  AdminDetailTableHeader,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminMetadataText,
  AdminSupportingText,
  AdminMainText,
  AdminMetric,
  AdminHeaderAction,
  AdminRowAction,
  AdminRowActionsButton,
  AdminLinkAction,
  AdminProfileAvatar,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  AdminFormSecondaryAction,
  adminFormInputClasses,
  adminFormSelectClasses,
  adminFormTextareaClasses,
  getAdminNoteAddedByDisplay,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
  ADMIN_TAB_CONTENT_OFFSET_CLASS,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
} from '../shared/adminUx';
import { DashboardIcon } from '../../../components/common/Dashboard';
import Profile from "../../../assets/img/User.png";
import { emitPublicationActivity } from '../../../data/changeHistoryEmitters';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import { getPostsForScenarios } from '../../../data/postModerationScenarios';

/** Flag ID | Case ID | Source | Reason | Severity | Case Status | Created On | Last Updated | Action */
const FLAGS_TABLE_GRID =
  'minmax(0, 0.7fr) minmax(0, 0.9fr) minmax(0, 0.85fr) minmax(0, 1.2fr) minmax(0, 0.5fr) minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

const getDetailTablePagination = (totalCount, currentPage) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);
  const startIndex = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
  return {
    totalPages,
    effectivePage,
    rangeStart: totalCount === 0 ? 0 : startIndex + 1,
    rangeEnd: Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount),
    showPagination: totalCount > CONFIG_LIST_PAGE_SIZE,
    paginate: (items) => items.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE),
  };
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('en-AU');
};

/** Aligns with admin posts list wording (Screen 1). */
const displayPostStatusLabel = (post) => {
  const s = post?.status;
  if (s === 'Published' || s === 'Active') return 'Active';
  if (s === 'Pending' || s === 'Under Review') return 'Under Review';
  if (s === 'Draft') return 'Draft';
  if (s === 'Removed') return 'Removed';
  if (s === 'Deleted') return 'Deleted';
  return s || '—';
};

/** Spec-aligned Post Type (Post | Featured Post). */
const displayPostTypeLabel = (post) => {
  const type = (post?.tier || post?.postType || '').toString().trim();
  if (type === 'Featured' || type === 'Featured Post') return 'Featured Post';
  if (type === 'Standard' || type === 'Basic' || type === 'Post') return 'Post';
  return type || '—';
};

/** Open Flags: New or Active handling (not Decided). */
const isOpenFlagStatus = (status) => {
  const st = String(status || '').trim().toLowerCase();
  if (!st) return false;
  if (['decided', 'closed', 'resolved', 'cleared', 'approved'].includes(st)) return false;
  if (['new', 'active', 'under review', 'in review', 'open', 'pending', 'attached'].includes(st)) {
    return true;
  }
  return false;
};

/**
 * Any accepted open Flag hides the Post (including Low).
 * Draft / Removed / Deleted are always Hidden.
 */
const displayPostVisibilityLabel = (post) => {
  const v = (post?.visibility || '').toString().trim();
  if (v === 'Visible' || v === 'Hidden') return v;

  const status = displayPostStatusLabel(post);
  if (status === 'Removed' || status === 'Deleted' || status === 'Draft') return 'Hidden';

  const openFlags = Array.isArray(post?.flags) ? post.flags : [];
  const hasOpen = openFlags.some((f) =>
    isOpenFlagStatus(f.handlingStatus || f.status || f.flagStatus || f.caseStatus)
  );
  if (hasOpen) return 'Hidden';
  return 'Visible';
};

const normalizeFlagIdDisplay = (id) => {
  if (!id) return null;
  if (typeof id === 'string' && /^FLG-/i.test(id)) return id;
  const match = String(id).match(/(\d+)/);
  return match ? `FLG-${String(match[1]).padStart(5, '0')}` : `FLG-${String(id).slice(-4)}`;
};

const formatModerationCaseId = (id) => {
  if (!id) return null;
  const s = String(id).trim();
  if (/^MOA-/i.test(s)) return s.toUpperCase();
  if (/^MOD-DEC-/i.test(s)) return s.toUpperCase();
  if (/^(?:MODS|MOD)-/i.test(s)) {
    const digits = s.replace(/^(?:MODS|MOD)-?/i, '').replace(/\D/g, '');
    if (!digits) return s.toUpperCase();
    return `MOD-${digits.padStart(6, '0')}`;
  }
  const digits = s.replace(/\D/g, '');
  if (!digits) return s;
  return `MOD-${digits.padStart(6, '0')}`;
};

const countOpenFlagsOnPost = (post) => {
  const flags = post?.flags;
  if (!Array.isArray(flags) || !flags.length) return 0;
  return flags.filter((f) =>
    isOpenFlagStatus(f.handlingStatus || f.status || f.flagStatus || f.caseStatus)
  ).length;
};

const getLatestFlagRecord = (post) => {
  const flags = post?.flags;
  if (!Array.isArray(flags) || !flags.length) return null;
  const scored = flags.map((f, i) => ({
    f,
    t: new Date(f.updatedAt || f.lastUpdated || f.createdAt || f.date || 0).getTime() || i,
  }));
  scored.sort((a, b) => b.t - a.t);
  return scored[0]?.f || null;
};

/** Flag sources: User Flag or System Flag. */
const FLAG_SOURCE_LABELS = ['User Flag', 'System Flag'];

const normalizeFlagSourceLabel = (raw, ctx = {}) => {
  const from = String(raw || ctx.source || '').trim();
  const type = String(ctx.type || ctx.category || '').toLowerCase();
  const lowered = from.toLowerCase();

  if (FLAG_SOURCE_LABELS.includes(from)) return from;

  if (
    lowered === 'user flag' ||
    lowered === 'user report' ||
    lowered === 'customer' ||
    lowered === 'customer flag' ||
    lowered === 'business' ||
    lowered === 'business flag' ||
    lowered.includes('user report') ||
    lowered.includes('user flag') ||
    type.includes('user report')
  ) {
    return 'User Flag';
  }

  return 'System Flag';
};

/** Handling Status: New · Active · Decided. */
const normalizeHandlingStatus = (raw) => {
  const v = String(raw || '').trim();
  const lower = v.toLowerCase();
  if (['decided', 'resolved', 'cleared', 'approved', 'closed', 'escalated'].includes(lower)) {
    return 'Decided';
  }
  if (['active', 'under review', 'in review', 'attached'].includes(lower)) return 'Active';
  if (['new', 'open', 'pending'].includes(lower)) return 'New';
  if (v === 'New' || v === 'Active' || v === 'Decided') return v;
  return 'New';
};

const normalizeFlagSeverity = (raw) => {
  let sev = (raw || 'Medium').toString().trim();
  if (sev === 'Critical') sev = 'High';
  if (!['Low', 'Medium', 'High'].includes(sev)) sev = 'Medium';
  return sev;
};

const getRankingState = (createdAt, lastRefreshed) => {
  if (!lastRefreshed) return 'Stale';
  const now = new Date();
  const refreshedDate = new Date(lastRefreshed);
  const daysSinceRefresh = Math.floor((now - refreshedDate) / (1000 * 60 * 60 * 24));
  
  if (daysSinceRefresh <= 30) return 'Fresh';
  if (daysSinceRefresh <= 60) return 'Ageing';
  return 'Stale';
};

const calculateNextEligibleRefresh = (lastRefreshed) => {
  if (!lastRefreshed) return null;
  const refreshedDate = new Date(lastRefreshed);
  refreshedDate.setDate(refreshedDate.getDate() + 30);
  return refreshedDate;
};

const RANKING_STATE_CLASSES = {
  Fresh: 'bg-freshgreen text-white',
  Ageing: 'bg-warmyellow text-white',
  Stale: 'bg-alertred text-white',
};

const generatePostDetail = (postId) => {
  const now = new Date();
  const createdAt = new Date(now.getFullYear(), now.getMonth() - 2, 14, 11, 0);
  const refreshedAt = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 16);
  const lastUpdated = new Date(refreshedAt);
  lastUpdated.setDate(lastUpdated.getDate() + 5);

  return {
    postId,
    title: 'Blocked Drain Service Available',
    status: 'Under Review',
    postType: 'Post',
    tier: 'Standard',
    category: 'Plumbing',
    subcategory: 'Drainage',
    appliedKeywords: ['Blocked drain', 'Drain cleaning'],
    suburb: 'Werribee',
    region: 'VIC',
    postcode: '3030',
    serviceRadius: '20 km',
    location: 'Werribee, VIC',
    createdAt: createdAt.toISOString(),
    publishedOn: createdAt.toISOString(),
    lastUpdated: lastUpdated.toISOString(),
    lastRefreshed: refreshedAt.toISOString(),
    visibility: 'Hidden',
    totalRefreshes: 0,
    totalImages: 3,
    openFlagsCount: 2,
    openModerationCases: 2,
    totalFlags: 2,
    flagStatus: 'Under Review',
    lastModerationUpdate: null,
    moderationCaseId: 'MOD-000244',
    moderationCaseStatus: 'Active',
    thumbnail: 'https://picsum.photos/seed/post7856a/200/200',
    business: {
      id: 'BUS-6609',
      businessId: 'BUS-6609',
      name: 'QuickFlow Plumbing Services',
      profileUrl: '/admin/users/businesses/BUS-6609',
      verification: 'Verified',
      plan: 'Pro',
      accountStatus: 'Active',
      abn: '12345678901',
      memberSince: '2025-05-29',
    },
    businessAccountStatus: 'Active',
    description:
      'Blocked drain and general plumbing services available in Werribee and nearby suburbs. Same-day callouts are available where scheduling allows.',
    gallery: [
      { url: 'https://picsum.photos/seed/post7856a/640/400', flagged: false },
      { url: 'https://picsum.photos/seed/post7856b/640/400', flagged: false },
      { url: 'https://picsum.photos/seed/post7856c/640/400', flagged: false },
    ],
    media: { imageCount: 3, limit: 8 },
    engagement: {
      totalViews: 1600,
      uniqueVisitors: 0,
      totalSaves: 35,
      totalShares: 6,
      messages: 18,
      clicksToBusinessProfile: 0,
      conversionRate: 0,
      getInTouchClicks: 0,
    },
    refreshHistory: [],
    moderationLog: [],
    flags: [
      {
        flagId: 'FLG-52041',
        id: 'FLG-52041',
        source: 'User Flag',
        category: 'Moderation',
        trigger: 'Misleading service claim',
        reason: 'Misleading service claim',
        severity: 'Medium',
        status: 'Active',
        flagStatus: 'Under Review',
        handlingStatus: 'Active',
        caseStatus: 'Active',
        linkedCaseId: 'MOD-000244',
        linkedCaseType: 'Moderation',
        createdAt: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 8, 14).toISOString(),
        updatedAt: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 10, 15).toISOString(),
      },
      {
        flagId: 'FLG-52018',
        id: 'FLG-52018',
        source: 'System Flag',
        category: 'Spam',
        trigger: 'Repeated promotional wording',
        reason: 'Repeated promotional wording',
        severity: 'Low',
        status: 'Active',
        flagStatus: 'Under Review',
        handlingStatus: 'Active',
        caseStatus: 'Active',
        linkedCaseId: 'MOD-000238',
        linkedCaseType: 'Moderation',
        createdAt: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2, 17, 33).toISOString(),
        updatedAt: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 9, 20).toISOString(),
      },
    ],
    moderationCases: [],
    linkedRecords: {},
    refreshNotes: [],
    auditLogs: [],
    postActivity: [],
    notes: [],
  };
};

const PostDetail = () => {
  const { postId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postIdCopied, setPostIdCopied] = useState(false);
  const [businessIdCopied, setBusinessIdCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(() => (location.state?.activeTab === 'flags' ? 'flags' : 'overview'));
  const [flagsCurrentPage, setFlagsCurrentPage] = useState(1);
  const [previewMedia, setPreviewMedia] = useState(null);
  
  // Audit Logs state
  const [auditFilters, setAuditFilters] = useState({
    dateRange: 'All',
    actor: 'All',
    module: 'All',
    outcome: 'All',
  });
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [actorOpen, setActorOpen] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  
  // Admin Notes state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [viewingNote, setViewingNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [pinnedNotes, setPinnedNotes] = useState(new Set());
  const [noteRowActionsOpen, setNoteRowActionsOpen] = useState({});
  const noteRowActionsRefs = useRef({});
  const [noteForm, setNoteForm] = useState({
    category: '',
    noteText: ''
  });

  // Flags tab filters
  const [flagsFilters, setFlagsFilters] = useState({
    source: 'All',
    category: 'All',
    severity: 'All',
    status: 'All',
    dateRange: 'All'
  });

  // Flags filter dropdown states
  const [flagsSourceFilterOpen, setFlagsSourceFilterOpen] = useState(false);
  const [flagsCategoryFilterOpen, setFlagsCategoryFilterOpen] = useState(false);
  const [flagsSeverityFilterOpen, setFlagsSeverityFilterOpen] = useState(false);
  const [flagsStatusFilterOpen, setFlagsStatusFilterOpen] = useState(false);
  const [flagsDateRangeFilterOpen, setFlagsDateRangeFilterOpen] = useState(false);

  // Flags filter refs
  const flagsSourceFilterRef = useRef(null);
  const flagsCategoryFilterRef = useRef(null);
  const flagsSeverityFilterRef = useRef(null);
  const flagsStatusFilterRef = useRef(null);
  const flagsDateRangeFilterRef = useRef(null);
  
  const dateRangeRef = useRef(null);
  const actorRef = useRef(null);
  const moduleRef = useRef(null);
  const outcomeRef = useRef(null);
  
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(actorRef, () => setActorOpen(false));
  useClickOutside(moduleRef, () => setModuleOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));
  useClickOutside(flagsSourceFilterRef, () => setFlagsSourceFilterOpen(false));
  useClickOutside(flagsCategoryFilterRef, () => setFlagsCategoryFilterOpen(false));
  useClickOutside(flagsSeverityFilterRef, () => setFlagsSeverityFilterOpen(false));
  useClickOutside(flagsStatusFilterRef, () => setFlagsStatusFilterOpen(false));
  useClickOutside(flagsDateRangeFilterRef, () => setFlagsDateRangeFilterOpen(false));

  // Handle clicking outside to close all audit log filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const filterRefs = [dateRangeRef, actorRef, moduleRef, outcomeRef];
      const isOutsideAll = filterRefs.every(ref => {
        const el = ref?.current;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setDateRangeOpen(false);
        setActorOpen(false);
        setModuleOpen(false);
        setOutcomeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);
  
  // Handle clicking outside to close note row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(noteRowActionsRefs.current).every(ref => {
        const el = ref;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setNoteRowActionsOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleFilterChange = (filterType, value) => {
    setAuditFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Admin Notes handlers
  const openNotesModal = (note = null) => {
    if (note) {
      setEditingNote(note);
      setNoteForm({
        category: note.category || '',
        noteText: note.noteContent || note.content || ''
      });
    } else {
      setEditingNote(null);
      setNoteForm({
        category: '',
        noteText: ''
      });
    }
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setEditingNote(null);
    setNoteForm({
      category: '',
      noteText: ''
    });
  };

  const handleNotePin = (noteId) => {
    setPinnedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
    setNoteRowActionsOpen(prev => ({ ...prev, [noteId]: false }));
  };

  const toggleNoteRowActions = (noteId) => {
    setNoteRowActionsOpen((prev) => {
      if (prev[noteId]) return {};
      return { [noteId]: true };
    });
  };

  const handleNoteSave = () => {
    if (!noteForm.category || !noteForm.noteText) {
      alert('Category and Note text are required');
      return;
    }

    const now = new Date();
    const timestamp = `${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    const date = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const targetId = post?.postId || post?.id || postId;
    const actionLabel = editingNote ? 'Note updated' : 'Note added';
    
    // In a real app, this would be an API call
    if (editingNote) {
      // Update existing note
      setPost(prev => ({
        ...prev,
        notes: prev.notes.map(note => 
          note.id === editingNote.id 
            ? {
                ...note,
                category: noteForm.category,
                content: noteForm.noteText,
                noteContent: noteForm.noteText,
                timestamp: timestamp,
                date: date,
                createdAt: now.toISOString()
              }
            : note
        )
      }));
    } else {
      // Add new note
      const newNote = {
        id: `note-${Date.now()}`,
        timestamp: timestamp,
        date: date,
        createdAt: now.toISOString(),
        author: 'Rachel Brown',
        adminName: 'Moderator — Rachel Brown',
        category: noteForm.category,
        content: noteForm.noteText,
        noteContent: noteForm.noteText
      };
      
      setPost(prev => ({
        ...prev,
        notes: [newNote, ...(prev.notes || [])]
      }));
    }

    if (targetId) {
      try {
        emitPublicationActivity({
          targetType: 'Post',
          targetId,
          targetName: post?.title || post?.postTitle || targetId,
          actionLabel,
          outcome: `Rachel Brown; ${noteForm.category}`,
          actor: 'Rachel Brown',
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    }
    
    closeNotesModal();
  };

  const handleViewNote = (note) => {
    setViewingNote(note);
    setNoteRowActionsOpen(prev => ({ ...prev, [note.id]: false }));
  };

  const closeViewNoteModal = () => {
    setViewingNote(null);
  };

  // Helper functions for refs
  const getRefDisplay = (ref) => {
    if (!ref || !ref.id) return '—';
    const prefix = ref.type === 'post' ? 'POST-' : ref.type === 'image' ? 'IMG-' : ref.type === 'business' ? 'BUS-' : '';
    return prefix ? `${prefix}${ref.id.replace(/^[A-Z]+-?/i, '')}` : ref.id;
  };

  const getRefLink = (ref) => {
    if (!ref || !ref.id) return null;
    switch (ref.type) {
      case 'post':
        return `/admin/posts/${ref.id}`;
      case 'image':
        return null; // Images don't have detail pages
      case 'business':
        return `/admin/users/businesses/${ref.id}`;
      default:
        return null;
    }
  };


  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 350));

      const passedPost = location.state?.post;
      let foundPost = passedPost;
      if (!foundPost && postId) {
        const scenarioPosts = getPostsForScenarios();
        const normalized = String(postId).replace(/^POST-?/i, '');
        foundPost = scenarioPosts.find(
          (p) => String(p.postId || p.id).replace(/^POST-?/i, '') === normalized
        ) || null;
      }

      if (foundPost) {
        const defaultPost = generatePostDetail(postId || foundPost.postId || foundPost.id || 'P0001');
        const flags = foundPost.flags || [];
        const openMod = flags.filter((f) => f.status === 'Under Review' || f.flagStatus === 'Under Review').length;
        const postData = {
          ...defaultPost,
          postId: foundPost.postId || foundPost.id || postId,
          title: foundPost.title,
          description: foundPost.description ?? defaultPost.description,
          gallery: foundPost.gallery && foundPost.gallery.length > 0 ? foundPost.gallery : defaultPost.gallery,
          flags: foundPost.flags,
          moderationCases: [],
          moderationLog: [],
          refreshHistory: [],
          moderationCaseId: foundPost.moderationCaseId ?? flags[0]?.linkedCaseId ?? null,
          moderationCaseStatus: foundPost.moderationCaseStatus ?? null,
          linkedRecords: {
            live: foundPost.publicLink || '',
            business: foundPost.businessId ? `/admin/users/businesses/${foundPost.businessId}` : '',
          },
          status: foundPost.status,
          visibility: foundPost.visibility,
          postType: foundPost.tier || foundPost.postType,
          tier: foundPost.tier || foundPost.postType,
          category: foundPost.category,
          subcategory: foundPost.subcategory,
          appliedKeywords: foundPost.appliedKeywords || foundPost.keywords || [],
          suburb: foundPost.suburb,
          region: foundPost.region,
          location: foundPost.suburb && foundPost.region ? `${foundPost.suburb}, ${foundPost.region}` : (foundPost.location || '—'),
          postcode: foundPost.postcode,
          serviceRadius: foundPost.serviceRadius || null,
          publicLink: foundPost.publicLink || '',
          createdAt: foundPost.createdAt,
          publishedOn: foundPost.publishedOn || foundPost.createdAt,
          removedOn: foundPost.removedOn || null,
          deletedOn: foundPost.deletedOn || null,
          lastUpdated: foundPost.lastUpdated,
          lastRefreshed: foundPost.lastRefreshed,
          flagStatus: foundPost.flagStatus || 'None',
          openModerationCases: foundPost.openModerationCases ?? openMod,
          totalFlags: foundPost.totalFlags ?? flags.length,
          lastModerationUpdate: foundPost.lastModerationUpdate || null,
          thumbnail:
            foundPost.thumbnail ||
            foundPost.gallery?.[0]?.url ||
            foundPost.images?.[0]?.url ||
            defaultPost.thumbnail,
          logo: foundPost.logo || null,
          business: {
            ...defaultPost.business,
            id: foundPost.businessId,
            businessId: foundPost.businessId,
            name: foundPost.businessName,
            profileUrl: foundPost.businessId ? `/admin/users/businesses/${foundPost.businessId}` : '/admin/users/businesses',
            verification: foundPost.businessVerification || 'Not Submitted',
            plan: foundPost.businessPlan || ((foundPost.tier === 'Featured' || foundPost.postType === 'Featured') ? 'Pro' : 'Basic'),
            accountStatus: foundPost.businessAccountStatus || 'Active',
            logo: foundPost.logo || null,
            abn: foundPost.business?.abn || defaultPost.business?.abn || '12345678901',
            memberSince: foundPost.businessMemberSince || foundPost.business?.memberSince || defaultPost.business?.memberSince,
          },
          engagement: {
            ...defaultPost.engagement,
            totalViews: foundPost.views ?? defaultPost.engagement?.totalViews ?? 0,
            totalSaves: foundPost.saves ?? defaultPost.engagement?.totalSaves ?? 0,
            totalShares: foundPost.shares ?? defaultPost.engagement?.totalShares ?? 0,
            messages: foundPost.messages ?? foundPost.engagement?.messages ?? defaultPost.engagement?.messages ?? 0,
            uniqueVisitors: foundPost.uniqueVisitors ?? defaultPost.engagement?.uniqueVisitors ?? 0,
            clicksToBusinessProfile: foundPost.clicksToBusinessProfile ?? defaultPost.engagement?.clicksToBusinessProfile ?? 0,
            quoteRequests: foundPost.quoteRequests ?? foundPost.engagement?.quoteRequests ?? defaultPost.engagement?.quoteRequests ?? 0,
            conversionRate: foundPost.conversionRate ?? defaultPost.engagement?.conversionRate ?? 0,
            getInTouchClicks: foundPost.getInTouchClicks ?? defaultPost.engagement?.getInTouchClicks ?? 0,
          },
        };
        setPost(postData);
      } else {
        setPost(generatePostDetail(postId || 'P0001'));
      }

      setLoading(false);
    };

    load();
  }, [postId, location.state]);

  // Open Flags tab when navigated with state.activeTab === 'flags' (e.g. from moderation queue)
  useEffect(() => {
    const tabFromState = location.state?.activeTab;
    if (tabFromState === 'flags') {
      setActiveTab('flags');
    }
  }, [location.state?.activeTab]);

  useEffect(() => {
    setFlagsCurrentPage(1);
  }, [postId]);

  useEffect(() => {
    if (!post?.flags?.length) return;
    const pages = Math.max(1, Math.ceil(post.flags.length / CONFIG_LIST_PAGE_SIZE));
    setFlagsCurrentPage((p) => Math.min(p, pages));
  }, [post?.flags?.length]);

  const tabs = useMemo(() => {
    if (!post) return [];
    return [
      { id: 'overview', label: 'Overview' },
      { id: 'content', label: 'Content & Media' },
      { id: 'flags', label: 'Flags' },
    ];
  }, [post]);

  const handleViewBusiness = () => {
    if (post?.linkedRecords?.business) {
      window.open(post.linkedRecords.business, '_blank', 'noopener');
    }
  };

  if (loading || !post) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText className="text-offblack">Loading post details…</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  const overviewStatusLabel = displayPostStatusLabel(post);
  const postTypeLabel = displayPostTypeLabel(post);
  const visibilityLabel = displayPostVisibilityLabel(post);
  const headerPostId = post.postId
    ? post.postId.startsWith('POST-')
      ? post.postId
      : `POST-${String(post.postId).replace(/^P/i, '')}`
    : '—';
  const headerPostImage =
    post.thumbnail || post.gallery?.[0]?.url || post.images?.[0]?.url || Profile;

  const handleCopyPostId = async (event) => {
    event?.stopPropagation?.();
    if (!headerPostId || headerPostId === '—') return;
    try {
      await navigator.clipboard.writeText(headerPostId);
      setPostIdCopied(true);
      window.setTimeout(() => setPostIdCopied(false), 1500);
    } catch {
      // Clipboard unavailable — leave UI unchanged
    }
  };

  const handleCopyBusinessId = async (value) => {
    if (!value || value === '—') return;
    try {
      await navigator.clipboard.writeText(value);
      setBusinessIdCopied(true);
      window.setTimeout(() => setBusinessIdCopied(false), 1500);
    } catch {
      // Clipboard unavailable — leave UI unchanged
    }
  };

  const renderResultValue = (value) => {
    if (!value) {
      return <AdminTableBody>—</AdminTableBody>;
    }

    const segments = value.split('→').map((segment) => segment.trim()).filter(Boolean);

    if (segments.length <= 1) {
      return <AdminTableBody>{value}</AdminTableBody>;
    }

    return (
      <div className="flex flex-wrap items-center gap-2">
        {segments.map((segment, index) => (
          <React.Fragment key={`${segment}-${index}`}>
            {index > 0 && <AdminMetadataText className="text-mediumgray">→</AdminMetadataText>}
            <AdminTableBody className="whitespace-nowrap">{segment}</AdminTableBody>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    const dateStr = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} · ${timeStr}`;
  };

  const renderOverview = () => {
    const businessIdDisplay = post.business?.businessId || post.business?.id;
    const businessIdFormatted = businessIdDisplay
      ? String(businessIdDisplay).startsWith('BUS-')
        ? businessIdDisplay
        : `BUS-${String(businessIdDisplay).replace(/^BUS-?/i, '')}`
      : '—';

    const openFlags = countOpenFlagsOnPost(post);
    const messagesCount = Number(post.engagement?.messages ?? post.messages ?? 0);
    const appliedKeywords = Array.isArray(post.appliedKeywords)
      ? post.appliedKeywords
      : Array.isArray(post.keywords)
        ? post.keywords
        : [];
    const keywordsDisplay =
      appliedKeywords.length > 0
        ? appliedKeywords.map((k) => String(k).trim()).filter(Boolean).join(' · ')
        : '—';
    const serviceRadiusRaw = post.serviceRadius || post.service_radius || '';
    const serviceRadiusDisplay = serviceRadiusRaw
      ? /km/i.test(String(serviceRadiusRaw))
        ? String(serviceRadiusRaw)
        : `${serviceRadiusRaw} km`
      : '—';
    const formatOverviewBadgeValue = (value) => {
      const raw = String(value ?? '').trim();
      if (!raw || raw === '—') return '';
      if (raw.toUpperCase() === 'PRO') return 'Pro';
      if (raw.toLowerCase() === 'basic') return 'Basic';
      if (raw.toLowerCase() === 'premium') return 'Premium';
      if (raw.toLowerCase() === 'verified') return 'Verified';
      if (raw.toLowerCase() === 'pending') return 'Pending';
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    };
    const accountStatus = formatOverviewBadgeValue(
      post.businessAccountStatus || post.business?.accountStatus
    );
    const verificationStatus = formatOverviewBadgeValue(
      post.business?.verification || post.businessVerification
    );
    const currentPlan = formatOverviewBadgeValue(post.business?.plan || post.businessPlan);
    const postIdForFilter = headerPostId !== '—' ? headerPostId : post.postId || postId;

    const overviewField = (label, value) => (
      <div className="space-y-1 min-w-0">
        <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
        <AdminDetailFieldValue className="break-words leading-snug">
          {value ?? '—'}
        </AdminDetailFieldValue>
      </div>
    );

    const overviewBadgeField = (label, value) => (
      <div className="space-y-1 min-w-0">
        <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
        {value ? (
          <div>
            <AdminStatusBadge status={value} label={value} />
          </div>
        ) : (
          <AdminDetailFieldValue>—</AdminDetailFieldValue>
        )}
      </div>
    );

    const overviewLinkedField = ({ label, valueText, enabled, onNavigate }) => (
      <div className="space-y-1 min-w-0">
        <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
        {enabled ? (
          <AdminDetailFieldValue className="break-words leading-snug">
            <AdminLinkAction type="button" onClick={onNavigate} className="tabular-nums text-left whitespace-normal break-words">
              {valueText}
            </AdminLinkAction>
          </AdminDetailFieldValue>
        ) : (
          <AdminDetailFieldValue className="tabular-nums text-mediumgray break-words leading-snug">
            {valueText}
          </AdminDetailFieldValue>
        )}
      </div>
    );

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Column 1 — Classification, Location, Post Details */}
          <div className="space-y-3 min-w-0">
            <div className="space-y-1 min-w-0">
              <AdminDetailFieldLabel>Post ID</AdminDetailFieldLabel>
              <div className="flex items-center gap-2 min-w-0">
                <AdminDetailFieldValue as="div" className="truncate min-w-0">
                  {headerPostId || '—'}
                </AdminDetailFieldValue>
                {headerPostId && headerPostId !== '—' ? (
                  <button
                    type="button"
                    onClick={handleCopyPostId}
                    className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
                    title={postIdCopied ? 'Copied' : 'Copy Post ID'}
                    aria-label={postIdCopied ? 'Copied' : 'Copy Post ID'}
                  >
                    <i
                      className={`fi ${
                        postIdCopied ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
                      } text-xs leading-none`}
                      aria-hidden
                    />
                  </button>
                ) : null}
              </div>
            </div>
            {overviewBadgeField('Status', overviewStatusLabel)}
            <div className="space-y-1 min-w-0">
              <AdminDetailFieldLabel>Post Type</AdminDetailFieldLabel>
              <AdminDetailFieldValue
                className={
                  postTypeLabel === 'Featured Post' ? 'text-freshgreen' : undefined
                }
              >
                {postTypeLabel || '—'}
              </AdminDetailFieldValue>
            </div>
            {overviewField('Trade Category', post.category || '—')}
            {overviewField('Subcategory', post.subcategory || '—')}
            {overviewField('Applied Keywords', keywordsDisplay)}
            {overviewField('State', post.region || post.state || '—')}
            {overviewField('Suburb', post.suburb || '—')}
            {overviewField('Postcode', post.postcode || post.postCode || '—')}
            {overviewField('Service Radius', serviceRadiusDisplay)}
            {overviewBadgeField('Visibility', visibilityLabel)}
            {overviewField('Created On', formatDateTime(post.createdAt))}
            {overviewField('Published On', formatDateTime(post.publishedOn || post.createdAt))}
            {overviewField('Last Updated', formatDateTime(post.lastUpdated || post.lastRefreshed))}
            {overviewStatusLabel === 'Removed'
              ? overviewField('Removed On', formatDateTime(post.removedOn))
              : null}
            {overviewStatusLabel === 'Deleted'
              ? overviewField('Deleted On', formatDateTime(post.deletedOn))
              : null}
          </div>

          {/* Column 2 — Business Summary */}
          <div className="space-y-3 min-w-0">
            <div className="space-y-1 min-w-0">
              <AdminDetailFieldLabel>Business ID</AdminDetailFieldLabel>
              <AdminDetailFieldValue as="div" className="inline-flex items-center gap-1.5 min-w-0">
                <span className="truncate" title={businessIdFormatted}>{businessIdFormatted}</span>
                {businessIdFormatted && businessIdFormatted !== '—' ? (
                  <button
                    type="button"
                    onClick={() => handleCopyBusinessId(businessIdFormatted)}
                    className="inline-flex items-center justify-center text-mediumgray hover:text-primaryblue shrink-0"
                    title={businessIdCopied ? 'Copied' : 'Copy Business ID'}
                    aria-label={businessIdCopied ? 'Copied' : 'Copy Business ID'}
                  >
                    <i
                      className={`fi ${businessIdCopied ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'} text-xs leading-none`}
                      aria-hidden
                    />
                  </button>
                ) : null}
              </AdminDetailFieldValue>
            </div>
            {overviewField('Business Name', post.business?.name || '—')}
            {overviewBadgeField('Account Status', accountStatus)}
            {overviewBadgeField('Verification Status', verificationStatus)}
            {overviewBadgeField('Current Plan', currentPlan)}
            {overviewLinkedField({
              label: 'Open Business',
              valueText: 'View',
              enabled: Boolean(businessIdDisplay),
              onNavigate: () => navigate(`/admin/users/businesses/${businessIdDisplay}`),
            })}
          </div>

          {/* Column 3 — Engagement and Connected Activity */}
          <div className="space-y-3 min-w-0">
            {overviewField('Views', formatNumber(post.engagement?.totalViews ?? post.views ?? 0))}
            {overviewField('Saves', formatNumber(post.engagement?.totalSaves ?? post.saves ?? 0))}
            {overviewField('Shares', formatNumber(post.engagement?.totalShares ?? post.shares ?? 0))}
            {overviewLinkedField({
              label: 'Messages',
              valueText: formatNumber(messagesCount),
              enabled: messagesCount > 0,
              onNavigate: () =>
                navigate('/admin/communication/all', {
                  state: { postId: postIdForFilter },
                }),
            })}
          </div>

          {/* Column 4 — Open Flags */}
          <div className="space-y-3 min-w-0">
            {overviewLinkedField({
              label: 'Open Flags',
              valueText: String(openFlags),
              enabled: true,
              onNavigate: () => setActiveTab('flags'),
            })}
          </div>
        </div>
    );
  };

  const renderContent = () => (
    <>
      <div className="space-y-6">
        <div className="space-y-3">
          <AdminDetailFieldLabel>Description</AdminDetailFieldLabel>
          <AdminMainText className="text-offblack whitespace-pre-line">
            {post.description}
          </AdminMainText>
        </div>

        <div className="space-y-4">
          <AdminDetailFieldLabel>Images</AdminDetailFieldLabel>
          {post.gallery && post.gallery.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {post.gallery.map((item, index) => (
                <button key={item.url || index} type="button" onClick={() => setPreviewMedia(item)} className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue cursor-pointer hover:opacity-90 transition-opacity">
                  <img
                    src={item.url}
                    alt={`Post image ${index + 1}`}
                    className="h-32 w-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/300?text=Image';
                    }}
                  />
                  {item.flagged && (
                    <span className="absolute top-2 right-2 px-2 py-1 rounded-full bg-alertred text-white text-[10px] uppercase tracking-wide">
                      Flagged
                    </span>
                  )}
                  <span className="absolute left-2 bottom-2 px-2 py-0.5 rounded bg-white/90 text-[11px] text-offblack">
                    Display {index + 1}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <AdminSupportingText className="text-mediumgray">
              No images available
            </AdminSupportingText>
          )}
        </div>
      </div>

      {/* Full Image Viewer Modal */}
      {previewMedia && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="relative max-w-6xl w-full max-h-[90vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="absolute -top-10 right-0 text-white hover:text-primaryblue text-sm font-medium transition-colors"
              onClick={() => setPreviewMedia(null)}
            >
              Close
            </button>
            <div className="relative rounded-lg overflow-hidden bg-white">
              <img 
                src={previewMedia.url} 
                alt="Full image view" 
                className="w-full h-full object-contain max-h-[90vh]" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/800?text=Image+Not+Available';
                }}
              />
              {previewMedia.flagged && (
                <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-alertred text-white text-xs uppercase tracking-wide">
                  Flagged
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderEngagement = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[
        { label: 'Views', value: post.engagement?.totalViews },
        { label: 'Unique visitors', value: post.engagement?.uniqueVisitors },
        { label: 'Saves', value: post.engagement?.totalSaves },
        { label: 'Shares', value: post.engagement?.totalShares },
        { label: 'Clicks to business profile', value: post.engagement?.clicksToBusinessProfile },
        { label: 'Quote requests from this post', value: post.engagement?.quoteRequests },
      ].map(({ label, value }) => (
        <DashboardMainContainer key={label} className={ADMIN_METRIC_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack mb-2">{label}</AdminSupportingText>
          <AdminMetric size="large" className="text-offblack mb-2 block">
            {formatNumber(value) || '—'}
          </AdminMetric>
        </DashboardMainContainer>
      ))}

      <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
        <AdminSupportingText className="text-offblack mb-2">Conversion rate</AdminSupportingText>
        <AdminMetric size="large" className="text-offblack mb-2 block">
          {post.engagement?.conversionRate !== undefined && post.engagement?.conversionRate !== null
            ? `${post.engagement.conversionRate.toFixed(1)}%`
            : '—'}
        </AdminMetric>
      </DashboardMainContainer>
    </div>
  );

  const buildPostFlagRows = (p) => {
    if (!p?.flags?.length) return [];
    const normalizeFlagId = (id, index = 0) => {
      if (!id) return `FLG-${String(10000 + index).padStart(5, '0')}`;
      if (typeof id === 'string' && id.match(/^FLG-/i)) return id;
      const match = String(id).match(/(\d+)/);
      if (match) return `FLG-${String(match[1]).padStart(5, '0')}`;
      return `FLG-${String(10000 + index).padStart(5, '0')}`;
    };
    return p.flags.map((flag, index) => {
      const displayFlagId = normalizeFlagId(flag.flagId || flag.id, index);
      const linkedCaseRaw =
        flag.linkedCaseId ||
        flag.caseId ||
        flag.handlingRecordId ||
        p.moderationCaseId ||
        null;
      const caseId = linkedCaseRaw ? formatModerationCaseId(linkedCaseRaw) : null;
      const handlingStatus = normalizeHandlingStatus(
        flag.handlingStatus ||
          flag.caseStatus ||
          flag.status ||
          flag.flagStatus ||
          p.moderationCaseStatus
      );
      const isDecided = handlingStatus === 'Decided';
      const handlingRecord = caseId || '—';
      return {
        key: `${displayFlagId}-${index}`,
        flagId: displayFlagId,
        source: normalizeFlagSourceLabel(flag.source || flag.raisedBy, flag),
        reason:
          (flag.reason || flag.trigger || flag.type || flag.object || '—').toString().trim() || '—',
        severity: normalizeFlagSeverity(flag.severity),
        handlingRecord,
        handlingRecordId: caseId,
        handlingStatus,
        createdAt: flag.createdAt || flag.date || p.createdAt,
        updatedAt: flag.lastUpdated || flag.updatedAt || flag.createdAt || flag.date || p.createdAt,
        actionLabel: caseId ? 'Open Case' : null,
        actionPath: caseId
          ? `/admin/moderation/queue/${encodeURIComponent(caseId)}`
          : null,
        openFromArchive: isDecided,
      };
    });
  };

  const handleFlagRowAction = (flag) => {
    if (!flag?.actionPath) return;
    navigate(flag.actionPath, {
      state: {
        from: location.pathname,
        flagId: flag.flagId,
        handlingRecordId: flag.handlingRecordId,
        postId: post?.postId || postId,
        fromArchive: !!flag.openFromArchive,
        readOnly: !!flag.openFromArchive,
      },
    });
  };

  const renderFlags = () => {
    if (!post) return null;

    const postFlagRows = buildPostFlagRows(post);
    const flagsTablePagination = getDetailTablePagination(postFlagRows.length, flagsCurrentPage);
    const paginatedFlagRows = flagsTablePagination.paginate(postFlagRows);

    return (
      <div className="space-y-6 min-w-0">
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="w-full min-w-0 max-w-full">
            <div
              className="grid min-w-0 py-3 text-offblack border-b border-lightgray gap-x-2 sm:gap-x-3 gap-y-2 px-4"
              style={{ gridTemplateColumns: FLAGS_TABLE_GRID }}
            >
              <AdminDetailTableHeader className="min-w-0 break-words">Flag ID</AdminDetailTableHeader>
              <AdminDetailTableHeader className="min-w-0 break-words">Case ID</AdminDetailTableHeader>
              <AdminDetailTableHeader className="min-w-0 break-words">Source</AdminDetailTableHeader>
              <AdminDetailTableHeader className="min-w-0 break-words">Reason</AdminDetailTableHeader>
              <AdminDetailTableHeader className="min-w-0 break-words">Severity</AdminDetailTableHeader>
              <AdminDetailTableHeader className="min-w-0 break-words">Case Status</AdminDetailTableHeader>
              <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Created On</AdminDetailTableHeader>
              <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Last Updated</AdminDetailTableHeader>
              <AdminDetailTableHeader className="min-w-0 break-words">Action</AdminDetailTableHeader>
            </div>

            {paginatedFlagRows.length > 0 ? (
              paginatedFlagRows.map((flag) => (
                <div
                  key={flag.key}
                  className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                  style={{ gridTemplateColumns: FLAGS_TABLE_GRID }}
                >
                  <AdminTableBody className="min-w-0 break-words leading-snug">{flag.flagId}</AdminTableBody>
                  <AdminTableBody className="min-w-0 break-words break-all leading-snug">
                    {flag.handlingRecord || '—'}
                  </AdminTableBody>
                  <AdminTableBody className="min-w-0 break-words leading-snug">{flag.source || '—'}</AdminTableBody>
                  <AdminTableBody
                    className="min-w-0 break-words break-all leading-snug"
                    title={flag.reason || ''}
                  >
                    {flag.reason}
                  </AdminTableBody>
                  <AdminTableBody emphasis className="min-w-0 break-words leading-snug">
                    {flag.severity || '—'}
                  </AdminTableBody>
                  <div className="flex items-start min-w-0">
                    <AdminFlagStatusBadge
                      status={flag.handlingStatus}
                      label={flag.handlingStatus || '—'}
                    />
                  </div>
                  <AdminTableMeta className="min-w-0 break-words leading-snug">
                    {formatDateTime(flag.createdAt)}
                  </AdminTableMeta>
                  <AdminTableMeta className="min-w-0 break-words leading-snug">
                    {formatDateTime(flag.updatedAt)}
                  </AdminTableMeta>
                  <div className="flex items-start min-w-0">
                    {flag.actionLabel && flag.actionPath ? (
                      <button
                        type="button"
                        className="text-left bg-transparent border-0 p-0 cursor-pointer"
                        onClick={() => handleFlagRowAction(flag)}
                      >
                        <AdminTableBody className="text-offblack underline-offset-2 hover:underline">
                          {flag.actionLabel}
                        </AdminTableBody>
                      </button>
                    ) : (
                      <AdminTableBody>—</AdminTableBody>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center">
                <AdminSupportingText className="text-mediumgray">No flags found</AdminSupportingText>
              </div>
            )}
          </div>
        </DashboardMainContainer>
        <ConfigurationListPaginationFooter
          show={flagsTablePagination.showPagination}
          rangeStart={flagsTablePagination.rangeStart}
          rangeEnd={flagsTablePagination.rangeEnd}
          totalCount={postFlagRows.length}
          currentPage={flagsTablePagination.effectivePage}
          totalPages={flagsTablePagination.totalPages}
          onPrev={() => setFlagsCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() =>
            setFlagsCurrentPage((prev) => Math.min(flagsTablePagination.totalPages, prev + 1))
          }
          goToPage={setFlagsCurrentPage}
        />
      </div>
    );
  };

  const renderRefreshHistory = () => (
    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
      <div className="overflow-hidden">
        <div className="grid grid-cols-4 py-3 text-offblack border-b border-lightgray px-4">
          {['Date', 'Type', 'Result', 'Auto-check'].map((label) => (
            <div className="flex items-center" key={label}>
              <AdminTableHeader>{label}</AdminTableHeader>
            </div>
          ))}
        </div>

        {post.refreshHistory.map((entry) => (
          <div
            key={`${entry.date}-${entry.type}`}
            className="grid grid-cols-4 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0"
          >
            <div className="flex items-center">
              <AdminTableMeta>{formatDate(entry.date)}</AdminTableMeta>
            </div>
            <div className="flex items-center">
              <AdminTableBody>{entry.type}</AdminTableBody>
            </div>
            <div className="flex items-center">{renderResultValue(entry.result)}</div>
            <div className="flex items-center">
              <AdminTableBody>{entry.autoCheck}</AdminTableBody>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-1 px-4 pb-4">
        <AdminMetadataText className="text-mediumgray block">Admin cannot refresh posts.</AdminMetadataText>
        <AdminMetadataText className="text-mediumgray block">Refresh does not create penalties.</AdminMetadataText>
        <AdminMetadataText className="text-mediumgray block">Ranking may improve after refresh.</AdminMetadataText>
      </div>
    </DashboardMainContainer>
  );

  const renderModerationLog = () => (
    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
      <div className="overflow-hidden">
        <div className="grid grid-cols-4 py-3 text-offblack border-b border-lightgray px-4">
          {['Date', 'By', 'Action', 'Notes'].map((label) => (
            <div className="flex items-center" key={label}>
              <AdminTableHeader>{label}</AdminTableHeader>
            </div>
          ))}
        </div>

        {post.moderationLog.map((entry) => (
          <div
            key={`${entry.date}-${entry.action}`}
            className="grid grid-cols-4 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0"
          >
            <div className="flex items-center">
              <AdminTableMeta>{formatDate(entry.date)}</AdminTableMeta>
            </div>
            <div className="flex items-center">
              <AdminTableBody>{entry.by}</AdminTableBody>
            </div>
            <div className="flex items-center">
              <AdminTableBody>{entry.action}</AdminTableBody>
            </div>
            <div className="flex items-center">
              <AdminTableBody>{entry.notes}</AdminTableBody>
            </div>
          </div>
        ))}
      </div>
    </DashboardMainContainer>
  );


  const renderLinkedRecords = () => (
    <div className={ADMIN_TOOLBAR_CLASS}>
      <div className="flex flex-col sm:flex-row gap-3">
        <AdminHeaderAction type="button" className="!w-auto" onClick={handleViewBusiness}>
          View business profile
        </AdminHeaderAction>
      </div>
    </div>
  );


  const renderAdminNotes = () => {
    return (
      <div className="space-y-4">
          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex justify-end items-center w-full">
              <AdminHeaderAction type="button" onClick={() => openNotesModal()}>
                Add Note
              </AdminHeaderAction>
            </div>
          </div>
          
          {/* Notes Table */}
          <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
            {/* Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(150px, 1.5fr) minmax(150px, 1.5fr) minmax(120px, 1fr) minmax(300px, 3fr) minmax(50px, 0.5fr)' }}>
              {['Created at', 'Added by', 'Category', 'Note content'].map((label) => (
                <div className="flex items-center" key={label}>
                  <AdminTableHeader>{label}</AdminTableHeader>
                </div>
              ))}
              <div className="flex items-center justify-center">
                <AdminTableHeader>Actions</AdminTableHeader>
              </div>
            </div>
            
            {/* Rows - Sort pinned notes first, then by date */}
            {(() => {
              const sortedNotes = [...(post?.notes || [])].sort((a, b) => {
                const aPinned = pinnedNotes.has(a.id);
                const bPinned = pinnedNotes.has(b.id);
                if (aPinned && !bPinned) return -1;
                if (!aPinned && bPinned) return 1;
                if (aPinned && bPinned) {
                  // Both pinned: newest first
                  const aDate = new Date(a.createdAt || a.timestamp || 0);
                  const bDate = new Date(b.createdAt || b.timestamp || 0);
                  return bDate - aDate;
                }
                // Both unpinned: newest first
                const aDate = new Date(a.createdAt || a.timestamp || 0);
                const bDate = new Date(b.createdAt || b.timestamp || 0);
                return bDate - aDate;
              });

              return sortedNotes.length > 0 ? sortedNotes.map((note) => {
                const noteContent = note.noteContent || note.content || '';
                const createdAt = note.createdAt ? formatDateTime(note.createdAt) : (note.timestamp || note.date || '—');
                const addedByDisplay = getAdminNoteAddedByDisplay(note);
                const category = note.category || '—';
                const isPinned = pinnedNotes.has(note.id);

                return (
                  <div key={note.id} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(150px, 1.5fr) minmax(150px, 1.5fr) minmax(120px, 1fr) minmax(300px, 3fr) minmax(50px, 0.5fr)' }}>
                    <div className="flex items-center">
                      <AdminTableMeta>{createdAt}</AdminTableMeta>
                    </div>
                    <div className="flex items-center">
                      <AdminTableBody>{addedByDisplay}</AdminTableBody>
                    </div>
                    <div className="flex items-center">
                      <AdminTableBody>{category}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={noteContent}>
                        {noteContent}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="relative inline-flex" ref={(el) => { noteRowActionsRefs.current[note.id] = el; }}>
                        <AdminRowActionsButton
                          onClick={() => toggleNoteRowActions(note.id)}
                          title="Row actions"
                          aria-label="Row actions"
                        >
                          <DashboardIcon size="sm">
                            <i className="fi fi-rs-menu-dots-vertical text-offblack" aria-hidden />
                          </DashboardIcon>
                        </AdminRowActionsButton>
                        {noteRowActionsOpen[note.id] && (
                          <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                              <AdminRowMenuItem
                                onClick={() => handleViewNote(note)}
                              >
                                View note
                              </AdminRowMenuItem>
                              <AdminRowMenuItem
                                onClick={() => handleNotePin(note.id)}
                              >
                                {isPinned ? 'Unpin note' : 'Pin note'}
                              </AdminRowMenuItem>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div className="py-8 text-center">
                  <AdminSupportingText className="text-mediumgray">No notes yet</AdminSupportingText>
                </div>
              );
            })()}
          </DashboardMainContainer>

          {/* Notes Modal */}
          {notesModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeNotesModal}>
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <AdminPanelTitle>Add Note</AdminPanelTitle>
                  <button
                    onClick={closeNotesModal}
                    className="text-offblack hover:text-primaryblue"
                  >
                    <i className="fi fi-rr-cross dashboard-icon"></i>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Category - Required */}
                  <div>
                    <AdminDetailFieldLabel>
                      Category <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <select
                      value={noteForm.category}
                      onChange={(e) => setNoteForm(prev => ({ ...prev, category: e.target.value }))}
                      className={`${adminFormSelectClasses()} w-full`}
                      required
                    >
                      <option value="">Select category</option>
                      <option value="General">General</option>
                      <option value="Support">Support</option>
                      <option value="Moderation">Moderation</option>
                      <option value="Fraud">Fraud</option>
                      <option value="Security">Security</option>
                      <option value="Bug">Bug</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Note Text - Required */}
                  <div>
                    <AdminDetailFieldLabel>
                      Note text <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <textarea
                      value={noteForm.noteText}
                      onChange={(e) => setNoteForm(prev => ({ ...prev, noteText: e.target.value }))}
                      className={`${adminFormTextareaClasses()} w-full`}
                      placeholder="Enter note text..."
                      required
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4">
                    <AdminFormSecondaryAction type="button" onClick={closeNotesModal}>
                      Cancel
                    </AdminFormSecondaryAction>
                    <AdminHeaderAction type="button" onClick={handleNoteSave}>
                      Save Note
                    </AdminHeaderAction>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* View Note Modal */}
          {viewingNote && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeViewNoteModal}>
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <AdminPanelTitle>View Note</AdminPanelTitle>
                  <button
                    onClick={closeViewNoteModal}
                    className="text-offblack hover:text-primaryblue"
                  >
                    <i className="fi fi-rr-cross dashboard-icon"></i>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Category */}
                  <div>
                    <AdminDetailFieldLabel>Category</AdminDetailFieldLabel>
                    <div className={`${adminFormInputClasses(true)} w-full flex items-center`}>
                      {viewingNote.category || '—'}
                    </div>
                  </div>

                  {/* Note Text */}
                  <div>
                    <AdminDetailFieldLabel>Note text</AdminDetailFieldLabel>
                    <div className={`${adminFormTextareaClasses(true)} w-full whitespace-pre-wrap`}>
                      {viewingNote.noteContent || viewingNote.content || '—'}
                    </div>
                  </div>

                  {/* Note Metadata */}
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <AdminDetailFieldLabel>Created</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {viewingNote.createdAt ? formatDateTime(viewingNote.createdAt) : (viewingNote.timestamp || viewingNote.date || '—')}
                      </AdminDetailFieldValue>
                    </div>
                    <div>
                      <AdminDetailFieldLabel>Added By</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {viewingNote.author || (viewingNote.adminName ? viewingNote.adminName.replace(/^(Admin|Compliance|Support|Moderator) — /, '').replace(/^Admin: /, '').split(' (')[0].trim() : '') || viewingNote.adminName || '—'}
                      </AdminDetailFieldValue>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'content':
        return renderContent();
      case 'flags':
        return renderFlags();
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="tp-container-main">
      <div>
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <div className="flex items-center gap-4">
            <AdminProfileAvatar
              src={headerPostImage}
              alt={post.title || ''}
              onError={(e) => {
                e.target.src = Profile;
              }}
            />
            <div className="flex-1 min-w-0">
              <AdminPanelTitle className="truncate">{post.title}</AdminPanelTitle>
            </div>
          </div>
        </DashboardMainContainer>


        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          <div
            className={
              activeTab === 'flags'
                ? `${ADMIN_TAB_CONTENT_OFFSET_CLASS} w-full min-w-0`
                : ADMIN_TAB_CONTENT_CONTAINER_CLASS
            }
          >
            {renderActiveTab()}
          </div>
        </DashboardMainContainer>
      </div>
      </div>
    </AdminLayout>
  );
};

export default PostDetail;



